import React from "react";
//Limitless imports
import { useAppConfig } from "@limitless/common/hooks";
import { useGraphAPIHook } from "../../../portal";
import { uniqueArrayBy } from "@limitless/common/utils/array-helper/ArrayHelper";
import { orderMessages } from "@limitless/customer/hooks/customer-history/CustomerHistoryHookHelper";
import { GET_SUPPORT_HISTORY } from "../../apis/expert-support-api/ExpertSupportQueries";
import { useNotificationContext } from "@limitless/common/hooks";
const INITIAL_STATE = {
  activeMessages: [],
  previousMessages: [],
  loading: false
};
const isMessageActive = message => {
  if (message.state === "closed") {
    return false;
  }
  return true;
};
export const useExpertSupportConversationHistory = () => {
  const [history, setHistory] = React.useState(INITIAL_STATE);
  const [initialHistory, setInitialHistory] = React.useState(false);
  const [unReadMessages, setUnReadMessages] = React.useState([]);
  const {
    getExpertSupportDetails
  } = useAppConfig();
  const {
    privateSupport: {
      tenantName: privateTenant
    },
    publicSupport: {
      tenantName: publicTenant
    }
  } = getExpertSupportDetails();
  const {
    addFailureNotification
  } = useNotificationContext();
  const {
    fetchData
  } = useGraphAPIHook();
  const processMessages = messages => {
    messages.forEach(message => {
      var _customerData$categor;
      const customerData = JSON.parse(message.customerData);
      message.topic = customerData === null || customerData === void 0 ? void 0 : (_customerData$categor = customerData.category) === null || _customerData$categor === void 0 ? void 0 : _customerData$categor.Topic;
    });
    return messages;
  };
  const fetchAndProcessHistory = ({
    tenantNames,
    customerEmail,
    history,
    setHistory,
    setInitialHistory,
    unReadMessages,
    addFailureNotification,
    privateTenant = null
  }) => {
    setHistory({
      ...history,
      loading: true
    });
    setInitialHistory(false);
    const fetchDataForTenant = tenantName => {
      const variables = privateTenant ? {
        tenantName: privateTenant,
        customerEmail,
        requester: "customer",
        limit: 50,
        filters: {
          archived: true,
          escalated: true,
          expired: true,
          reported: false
        }
      } : {
        tenantName,
        customerEmail,
        requester: "customer",
        limit: 50,
        filters: {
          archived: true,
          escalated: true,
          expired: true,
          reported: false
        }
      };
      const options = {
        dataKey: "getSupportHistory",
        query: GET_SUPPORT_HISTORY,
        variables,
        functionName: "ExpertSupportConversationHistoryHook getSupportHistory"
      };
      return fetchData(options).then(result => result).catch(error => {
        throw error;
      });
    };
    Promise.all(tenantNames.map(fetchDataForTenant)).then(results => {
      const combinedResults = Array.isArray(results[0]) ? results.flat() : results;
      const uniqueHistoryMessages = uniqueArrayBy(combinedResults, "messageId");
      const sortedUniqueHistoryMessages = uniqueHistoryMessages.sort((a, b) => new Date(b.created) - new Date(a.created));
      const processedMessages = processMessages(sortedUniqueHistoryMessages);
      const activeMessages = [];
      const previousMessages = [];
      processedMessages.forEach(message => {
        if ((message === null || message === void 0 ? void 0 : message.dialogues.length) !== 0) {
          if (isMessageActive(message)) {
            activeMessages.push(message);
          } else {
            previousMessages.push(message);
          }
        }
      });
      if (unReadMessages.length > 0 && activeMessages.length > 0) {
        activeMessages.forEach(message => {
          unReadMessages.forEach(unReadMessage => {
            if (message.messageId === unReadMessage) {
              message.isUnreadMessage = true;
            }
          });
        });
      }
      setHistory({
        ...history,
        loading: false,
        activeMessages: orderMessages(activeMessages, message => {
          const lastDialogue = message === null || message === void 0 ? void 0 : message.dialogues[message.dialogues.length - 1];
          return lastDialogue.updated;
        }),
        previousMessages: orderMessages(previousMessages, "created")
      });
      setInitialHistory(true);
    }).catch(() => {
      addFailureNotification("Failed to load expert support history");
      setHistory({
        ...history,
        loading: false
      });
    });
  };
  const fetchSupportMessageHistory = customerEmail => {
    const tenantNames = [privateTenant, publicTenant]; // public and private tenant for fetchConversationHistory for CM
    fetchAndProcessHistory({
      tenantNames,
      customerEmail,
      history,
      setHistory,
      setInitialHistory,
      unReadMessages,
      addFailureNotification
    });
  };
  const fetchConversationHistory = customerEmail => {
    const tenantNames = [privateTenant]; // Only private tenant for fetchConversationHistory for EWA
    fetchAndProcessHistory({
      tenantNames,
      customerEmail,
      history,
      setHistory,
      setInitialHistory,
      unReadMessages,
      addFailureNotification,
      privateTenant
    });
  };
  const updateHistoryMessage = messageId => {
    if (!unReadMessages.includes(messageId)) {
      setUnReadMessages([...unReadMessages, messageId]);
    }
    history.activeMessages.forEach(message => {
      if (message.messageId === messageId) {
        message.isUnreadMessage = true;
      }
    });
    setHistory(history);
  };
  const unReadMessageCount = React.useMemo(() => unReadMessages.length, [unReadMessages]);
  return {
    history,
    fetchConversationHistory,
    unReadMessageCount,
    updateHistoryMessage,
    setHistory,
    initialHistory,
    unReadMessages,
    setUnReadMessages,
    fetchSupportMessageHistory
  };
};